// Global Instructions Rule Applied!
//
// useCommitmentForm — the state of the Commitment form for one
// fund_commitments row: a STANDALONE commitment (everything editable, Delete)
// or a POCKET of a consolidated commitment (only its two markers are its own;
// the rest is read-only and it cannot be deleted here). The carry marker
// ("Receives carried interest") is per member fund, so a pocket is offered it
// too (owner 2026-10-12).
//
// The investor entity (owner 2026-10-12): a standalone commitment can be moved
// to another investor of the same or another account, or to a new investor
// entity created under an account with the save - the Investor section of the
// New commitment form, on the edit form. The commitment row keeps its id, so
// its journals, calls and notices follow it.
//
// The form holds a draft and saves explicitly. An edit sends only what
// differs from the loaded record (fund__save_commitment leaves an absent key
// alone). Reducing the amount below what has been called comes back as a 409
// warning: the form asks, and saves again with force.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Modal } from 'antd'
import { toast } from 'react-toastify'

import { dismissErrorToast, showErrorToast } from '../../../core/utils/errorToast.tsx'
import { summariseFieldErrors } from '../../../core/utils/utilities.ts'
import { useNumberDisplay } from '../../../core/utils/inputNumberFormat.ts'
import type { FieldValue } from '../../../core/components/InputManager/types.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import type { RootState } from '../../../state_manager/store.ts'
import type { SelectListEntry } from '../../../state_manager/data_models/select_list_entry.ts'
import { TABLE_COMMITMENTS } from '../../../state_manager/globals.ts'
import { updateTabTitle } from '../../../store/slices/tabSlice'
import type { FundCommitmentDetail } from '../../../state_manager/data_models/commitment_form.ts'
import { saveFundCommitment, trashFundCommitment } from '../../funds/services/fundsService.ts'
import { fetchFundCommitmentDetail } from '../services/commitmentsService.ts'
import {
  CommitmentFieldKeys,
  EMPTY_CORE_DRAFT,
  EMPTY_NEW_INVESTOR_DRAFT,
  EMPTY_NOTICE_DRAFT,
  EMPTY_TERMS_DRAFT,
  applyCoreChange,
  applyTermsChange,
  buildPocketEditPayload,
  buildStandaloneEditPayload,
  commitmentAmountAllowed,
  coreFromFundCommitment,
  investorKeys,
  noticeFromFundCommitment,
  standaloneDraftChanged,
  termsFromFundCommitment,
  type CommitmentNoticeDraft,
  type NewInvestorDraft,
  type StandaloneDraft
} from '../utils/commitmentDrafts.ts'
import {
  NEW_INVESTOR_VALUE,
  getCarryRecipientField,
  getCommitmentFields,
  getGpField,
  getNewCommitmentInvestorFields,
  getNewInvestorFields
} from '../utils/commitmentFieldConfigs.ts'
import { useCommitmentFormOptions } from './useCommitmentFormOptions.ts'

const VALIDATION_TOAST_KEY = 'commitment-form-required'
const EMPTY_DRAFT: StandaloneDraft = { core: EMPTY_CORE_DRAFT, terms: EMPTY_TERMS_DRAFT, notice: EMPTY_NOTICE_DRAFT }

type AccountValue = { value: string; label: string }

/** The company select hands back an option object, an id, or nothing. */
const toAccountValue = (value: FieldValue): AccountValue | null => {
  if (value && typeof value === 'object' && !Array.isArray(value) && value.value) {
    return { value: String(value.value), label: String(value.label ?? value.name ?? '') }
  }
  if (typeof value === 'string' && value) return { value, label: '' }
  return null
}

const numberOrNull = (value: FieldValue): number | null => {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

const draftFromDetail = (detail: FundCommitmentDetail): StandaloneDraft => ({
  core: coreFromFundCommitment(detail),
  terms: termsFromFundCommitment(detail),
  notice: noticeFromFundCommitment(detail)
})

export const commitmentTabTitle = (investorName: string | null, fundName: string | null): string =>
  [investorName, fundName].filter(Boolean).join(' — ') || 'Commitment'

export function useCommitmentForm(recordId: string) {
  const dispatch = useDispatch()
  const { closeFormTab } = useTabManagerContext()
  // static Modal.confirm does not render under React 19 - hook form required
  const [modalApi, modalContextHolder] = Modal.useModal()
  const currencyText = useNumberDisplay('currency')
  const formTabKey = `${TABLE_COMMITMENTS}-form-edit-${recordId}`

  const classifications = useSelector((state: RootState) => state.globalConfigs?.investor_classifications)
  const sbicInvestorTypes = useSelector((state: RootState) => state.globalConfigs?.sbic_investor_types)

  // the investor entity: the account and investor of the loaded row to begin
  // with; another investor of the account, another account, or a new entity
  const [account, setAccount] = useState<AccountValue | null>(null)
  const [investor, setInvestor] = useState<string | null>(null)
  const [newInvestor, setNewInvestor] = useState<NewInvestorDraft>(EMPTY_NEW_INVESTOR_DRAFT)
  const nameTouchedRef = useRef(false)

  const { statusOptions, noticeSupplementOptions, investors, investorsReady } = useCommitmentFormOptions(
    account?.value ?? null
  )

  const [detail, setDetail] = useState<FundCommitmentDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [baseline, setBaseline] = useState<StandaloneDraft>(EMPTY_DRAFT)
  const [draft, setDraft] = useState<StandaloneDraft>(EMPTY_DRAFT)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [validationErrorsVersion, setValidationErrorsVersion] = useState(0)
  // InputManager keeps what was typed and ignores a prop value for an edited
  // field, so putting the fields back (Cancel, a reload) remounts it
  const [resetVersion, setResetVersion] = useState(0)

  const applyDetail = useCallback(
    (next: FundCommitmentDetail) => {
      const fresh = draftFromDetail(next)
      setDetail(next)
      setBaseline(fresh)
      setDraft(fresh)
      setAccount(next.accounts__id ? { value: next.accounts__id, label: next.accounts__name ?? '' } : null)
      setInvestor(next.investors__id ?? null)
      setNewInvestor(EMPTY_NEW_INVESTOR_DRAFT)
      nameTouchedRef.current = false
      setResetVersion((v) => v + 1)
      dispatch(
        updateTabTitle({
          key: `${TABLE_COMMITMENTS}-form-edit-${next.fund_commitments__id}`,
          title: commitmentTabTitle(next.investors__name, next.funds__name)
        })
      )
    },
    [dispatch]
  )

  const load = useCallback(async (): Promise<void> => {
    const res = await fetchFundCommitmentDetail(recordId, getAccessToken(store.getState().auth))
    if (!res.data) {
      setNotFound(res.notFound === true)
      setLoadError(res.error ?? null)
      setDetail(null)
      return
    }
    setNotFound(false)
    setLoadError(null)
    applyDetail(res.data)
  }, [recordId, applyDetail])

  // Cold-load (the hard-refresh path is the normal path: no list page fills a
  // slice first).
  useEffect(() => {
    let cancelled = false
    void (async () => {
      setLoading(true)
      await load()
      if (!cancelled) setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [load])

  const isPocket = detail?.fund_commitments__fund_group_commitment_id != null

  // the investor entity differs from the loaded row's: another investor, or a
  // new one (an account change alone, with the investor still to pick, counts)
  const entityChanged =
    detail != null &&
    !isPocket &&
    (investor !== (detail.investors__id ?? null) || (account?.value ?? null) !== (detail.accounts__id ?? null))

  // An account with no investor entity yet: the commitment creates one.
  useEffect(() => {
    if (!account || !investorsReady || investor != null || !entityChanged) return
    if (investors.length === 0) setInvestor(NEW_INVESTOR_VALUE)
  }, [account, investorsReady, investors, investor, entityChanged])

  // A new investor entity is usually named after its account.
  useEffect(() => {
    if (investor !== NEW_INVESTOR_VALUE || nameTouchedRef.current || !account?.label) return
    setNewInvestor((prev) => (prev.name ? prev : { ...prev, name: account.label }))
  }, [investor, account])

  const payload = useMemo(() => {
    if (!detail) return null
    if (isPocket) return buildPocketEditPayload(detail.fund_commitments__id, draft.core, baseline.core)
    const base = buildStandaloneEditPayload(detail.fund_commitments__id, draft, baseline)
    if (entityChanged && account && investor) {
      Object.assign(
        base,
        investorKeys(investor === NEW_INVESTOR_VALUE ? { accountId: account.value, newInvestor } : { investorId: investor })
      )
    }
    return base
  }, [detail, isPocket, draft, baseline, entityChanged, account, investor, newInvestor])
  // a pocket's own values are its two markers; a standalone commitment is
  // unsaved as soon as any field differs (an emptied required field too)
  const dirty =
    detail != null &&
    (isPocket
      ? draft.core.is_gp_commitment !== baseline.core.is_gp_commitment ||
        draft.core.is_carry_recipient !== baseline.core.is_carry_recipient
      : standaloneDraftChanged(draft, baseline) || entityChanged)

  /** Coming back to the tab: pick up what changed elsewhere, unless the user
   *  has unsaved changes here. */
  const refreshIfClean = useCallback(async () => {
    if (!dirty && !saving) await load()
  }, [dirty, saving, load])

  const commitmentFields = useMemo(
    () =>
      getCommitmentFields(draft.core, {
        statusOptions,
        currencySymbol: detail?.currencies__symbol,
        readOnly: isPocket
      }),
    [draft.core, statusOptions, detail, isPocket]
  )
  // the GP marker, then the carry marker (a pocket's own too: the row a
  // fund's carried interest goes to is chosen per member fund)
  const markerFields = useMemo(() => [...getGpField(draft.core), ...getCarryRecipientField(draft.core)], [draft.core])

  // the investor entity: the New commitment form's Investor section
  const investorOptions = useMemo<SelectListEntry[]>(
    () => investors.map((i) => ({ value: i.value, label: i.label })),
    [investors]
  )
  const entityFields = useMemo(
    () =>
      getNewCommitmentInvestorFields(
        { account, investor },
        { investorOptions, accountLocked: isPocket, investorLocked: isPocket }
      ),
    [account, investor, investorOptions, isPocket]
  )
  const newInvestorFields = useMemo(
    () =>
      investor === NEW_INVESTOR_VALUE
        ? getNewInvestorFields(newInvestor, {
            classificationOptions: (classifications ?? []).map((c) => ({ value: c.id, label: c.label })),
            sbicInvestorTypeOptions: (sbicInvestorTypes ?? []).map((t) => ({ value: t.id, label: t.label })),
            isSbic: detail?.funds__is_sbic === true
          })
        : [],
    [investor, newInvestor, classifications, sbicInvestorTypes, detail]
  )

  const clearError = useCallback((key: string) => {
    setValidationErrors((prev) => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const handleChange = useCallback(
    (key: string, value: FieldValue) => {
      switch (key) {
        case CommitmentFieldKeys.account: {
          const next = toAccountValue(value)
          setAccount(next)
          // another account has other investor entities: the pick starts over
          // (back to the loaded account: back to the loaded investor)
          setInvestor(next && next.value === (detail?.accounts__id ?? null) ? (detail?.investors__id ?? null) : null)
          clearError(key)
          clearError(CommitmentFieldKeys.investor)
          return
        }
        case CommitmentFieldKeys.investor:
          setInvestor(value == null || value === '' ? null : String(value))
          clearError(key)
          return
        case CommitmentFieldKeys.new_investor_name:
          nameTouchedRef.current = true
          setNewInvestor((prev) => ({ ...prev, name: value == null ? null : String(value) }))
          clearError(key)
          return
        case CommitmentFieldKeys.new_investor_legal_name:
          setNewInvestor((prev) => ({ ...prev, legal_name: value == null ? null : String(value) }))
          return
        case CommitmentFieldKeys.new_investor_classification:
          setNewInvestor((prev) => ({ ...prev, investor_classification_id: numberOrNull(value) }))
          return
        case CommitmentFieldKeys.new_investor_sbic_type:
          setNewInvestor((prev) => ({ ...prev, sbic_investor_type_id: numberOrNull(value) }))
          return
        default:
          break
      }
      setDraft((prev) => ({
        ...prev,
        core: applyCoreChange(prev.core, key, value),
        terms: applyTermsChange(prev.terms, key, value)
      }))
      clearError(key)
      // an amount of 0 is refused or not by the carry marker
      if (key === CommitmentFieldKeys.is_carry_recipient) clearError(CommitmentFieldKeys.amount)
    },
    [clearError, detail]
  )

  const handleNoticeChange = useCallback((notice: CommitmentNoticeDraft) => {
    setDraft((prev) => ({ ...prev, notice }))
  }, [])

  const handleCancel = useCallback(() => {
    dismissErrorToast(VALIDATION_TOAST_KEY)
    setDraft(baseline)
    setAccount(detail?.accounts__id ? { value: detail.accounts__id, label: detail.accounts__name ?? '' } : null)
    setInvestor(detail?.investors__id ?? null)
    setNewInvestor(EMPTY_NEW_INVESTOR_DRAFT)
    nameTouchedRef.current = false
    setValidationErrors({})
    setResetVersion((v) => v + 1)
  }, [baseline, detail])

  const handleClose = useCallback(() => {
    closeFormTab(formTabKey)
  }, [closeFormTab, formTabKey])

  const submit = useCallback(
    async (force: boolean): Promise<void> => {
      if (!payload) return
      setSaving(true)
      try {
        const res = await saveFundCommitment(payload, force, getAccessToken(store.getState().auth))
        if (res.success) {
          toast.success('Commitment saved')
          await load()
          return
        }
        if (res.belowCalledWarning) {
          const { newAmount, calledToDate } = res.belowCalledWarning
          modalApi.confirm({
            title: 'Commitment below called-to-date',
            content: `The new amount ${currencyText(newAmount)} is below the ${currencyText(calledToDate)} already called. Save anyway?`,
            okText: 'Save anyway',
            onOk: () => submit(true)
          })
          return
        }
        showErrorToast(res.error || 'Failed to save the commitment')
      } finally {
        setSaving(false)
      }
    },
    [payload, load, modalApi, currencyText]
  )

  const handleSave = useCallback(async () => {
    if (!isPocket) {
      // Required-field gate: per-field red text + one toast naming the fields,
      // and the InputManager scrolls to and focuses the first bad field.
      // (0 is an amount on the commitment that receives the carried interest)
      const errors: Record<string, string> = {}
      if (!commitmentAmountAllowed(draft.core.amount, draft.core.is_carry_recipient)) {
        errors[CommitmentFieldKeys.amount] = 'Commitment is required'
      }
      if (draft.core.status_id == null) errors[CommitmentFieldKeys.status_id] = 'Status is required'
      // the investor entity, when it is being changed
      if (entityChanged) {
        if (!account) errors[CommitmentFieldKeys.account] = 'Account is required'
        if (!investor) errors[CommitmentFieldKeys.investor] = 'Investor is required'
        else if (investor === NEW_INVESTOR_VALUE && !(newInvestor.name ?? '').trim()) {
          errors[CommitmentFieldKeys.new_investor_name] = 'Investor name is required'
        }
      }
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors)
        setValidationErrorsVersion((v) => v + 1)
        showErrorToast(summariseFieldErrors([errors]) ?? 'Please complete the required fields', {
          key: VALIDATION_TOAST_KEY
        })
        return
      }
    }
    dismissErrorToast(VALIDATION_TOAST_KEY)
    await submit(false)
  }, [isPocket, draft.core, submit, entityChanged, account, investor, newInvestor])

  const handleDelete = useCallback(() => {
    if (!detail) return
    modalApi.confirm({
      title: 'Delete commitment',
      content: `Delete ${detail.investors__name ?? 'this investor'}'s commitment to ${detail.funds__name ?? 'this fund'}?`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        setDeleting(true)
        try {
          const res = await trashFundCommitment(detail.fund_commitments__id, getAccessToken(store.getState().auth))
          if (res.success) {
            toast.success('Commitment deleted')
            closeFormTab(formTabKey)
          } else {
            showErrorToast(res.error || 'Failed to delete the commitment')
          }
        } finally {
          setDeleting(false)
        }
      }
    })
  }, [detail, modalApi, closeFormTab, formTabKey])

  return {
    detail,
    isPocket,
    loading,
    notFound,
    loadError,
    draft,
    commitmentFields,
    markerFields,
    /** the investor entity: Account, then Investor (another account's investors load with it) */
    entityFields,
    /** the new entity's own fields, under "New investor entity" */
    newInvestorFields,
    /** remounts the investor select when the account changes */
    accountKey: account?.value ?? 'none',
    noticeSupplementOptions,
    dirty,
    saving,
    deleting,
    validationErrors,
    validationErrorsVersion,
    resetVersion,
    modalContextHolder: modalContextHolder as React.ReactElement,
    handleChange,
    handleNoticeChange,
    handleCancel,
    handleClose,
    handleSave,
    handleDelete,
    refreshIfClean
  }
}
